import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight, Sparkles, Brain, Calendar, Image as ImageIcon, MessageSquare, BarChart3,
    Check, Target, Layers, ChevronDown, Zap
} from "lucide-react";
import { motion, useInView } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import CinematicNav from "@/components/CinematicNav";
import Footer from "@/components/Footer";
import ModuleAccent from "@/components/ModuleAccent";
import ADSWorkflowScene from "@/components/ads/ADSWorkflowScene";
import { useAuth } from "@/contexts/AuthContext";

const EASE = [0.22, 1, 0.36, 1];

// ===== HERO =====
function Hero() {
    const { user } = useAuth();
    const isLogged = !!user && typeof user === "object";
    return (
        <section className="relative px-5 md:px-12 pt-32 pb-24 md:pt-44 md:pb-28" data-testid="ads-hero">
            <div className="max-w-6xl mx-auto text-center relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: EASE }}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6"
                    style={{ background: "var(--mod-accent-soft)", border: "1px solid var(--mod-accent)" }}
                >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--mod-accent)", boxShadow: "0 0 8px var(--mod-accent-glow)" }} />
                    <span className="text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--mod-accent)", fontFamily: "JetBrains Mono, monospace" }}>
                        modulo · totalklickads · beta privata
                    </span>
                </motion.div>

                <motion.h1
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 1.1, ease: EASE }}
                    className="tracking-tight text-white"
                    style={{
                        fontFamily: "Geist, Outfit, sans-serif",
                        fontSize: "clamp(2.4rem, 6vw, 5rem)",
                        lineHeight: 1.02,
                        letterSpacing: "-0.035em",
                        fontWeight: 200,
                    }}
                >
                    La tua agenzia social.<br />
                    <span style={{ fontWeight: 600 }}>Sempre attiva. Sempre AI.</span>
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35, duration: 1, ease: EASE }}
                    className="mt-6 text-white/65 mx-auto"
                    style={{ fontFamily: "Outfit, sans-serif", fontSize: "clamp(1.05rem, 1.8vw, 1.25rem)", lineHeight: 1.55, maxWidth: "32em" }}
                >
                    Brand DNA strutturato, calendario editoriale, AI che genera copy e grafiche brand-coded.
                    Un solo cockpit per gestire decine di clienti senza perdere coerenza.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.55, duration: 1, ease: EASE }}
                    className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center"
                >
                    {isLogged ? (
                        <Link
                            to="/totalklickads/dashboard"
                            data-testid="ads-hero-cta-logged"
                            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-white text-[14px] transition hover:scale-[1.03]"
                            style={{
                                background: "linear-gradient(135deg, var(--mod-accent), var(--mod-accent-deep))",
                                fontFamily: "Geist, sans-serif",
                                letterSpacing: "0.04em",
                                boxShadow: "0 18px 60px -10px var(--mod-accent-glow)",
                            }}
                        >
                            Apri TotalKlickADS <ArrowRight size={16} />
                        </Link>
                    ) : (
                        <a
                            href="#waitlist"
                            data-testid="ads-hero-cta"
                            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-white text-[14px] transition hover:scale-[1.03]"
                            style={{
                                background: "linear-gradient(135deg, var(--mod-accent), var(--mod-accent-deep))",
                                fontFamily: "Geist, sans-serif",
                                letterSpacing: "0.04em",
                                boxShadow: "0 18px 60px -10px var(--mod-accent-glow)",
                            }}
                        >
                            Entra in waitlist <ArrowRight size={16} />
                        </a>
                    )}
                    <a
                        href="#features"
                        data-testid="ads-hero-cta-secondary"
                        className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-white/85 hover:text-white text-[14px] border border-white/15 hover:bg-white/5 transition"
                        style={{ fontFamily: "Geist, sans-serif", letterSpacing: "0.04em" }}
                    >
                        Esplora le funzionalità
                    </a>
                </motion.div>

                {!isLogged && (
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.85, duration: 1 }}
                        className="mt-4 text-[11.5px] text-white/45" style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                        50% di sconto a vita per i primi 100 iscritti
                    </motion.p>
                )}
            </div>

            {/* Visual demo BrandDNA → AI Output */}
            <motion.div
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85, duration: 1.1, ease: EASE }}
                className="mt-16 md:mt-20 max-w-5xl mx-auto px-4"
            >
                <BrandDNADemo />
            </motion.div>
        </section>
    );
}

function BrandDNADemo() {
    return (
        <div
            className="relative rounded-2xl overflow-hidden"
            style={{
                background: "rgba(15,18,36,0.65)",
                border: "1px solid rgba(255,255,255,0.08)",
                backdropFilter: "blur(20px) saturate(140%)",
                WebkitBackdropFilter: "blur(20px) saturate(140%)",
                boxShadow: "0 30px 80px -20px rgba(0,0,0,0.5)",
            }}
        >
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5">
                <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: "rgba(255,95,87,0.55)" }} />
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: "rgba(255,189,46,0.55)" }} />
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: "rgba(39,201,63,0.55)" }} />
                </div>
                <span className="text-[10px] uppercase tracking-[0.18em] text-white/30" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                    workflow · brand-coded ai
                </span>
            </div>
            <div className="grid md:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-white/5">
                <DemoStep n="01" title="Brand DNA" body="Logo · Palette · Tono di voce · Target · Hashtag · Do not use" icon={<Target size={14} />} />
                <DemoStep n="02" title="Brief breve" body='"Lancio nuovo blend etiopia, edizione limitata 200 confezioni"' icon={<MessageSquare size={14} />} accent />
                <DemoStep n="03" title="Multi-piattaforma output" body="6 post (IG, FB, LinkedIn, X, TikTok, Threads) + 4 grafiche brand-coded" icon={<Layers size={14} />} />
            </div>
        </div>
    );
}

function DemoStep({ n, title, body, icon, accent }) {
    return (
        <div className="p-5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-white/45 mb-2" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                <span className={accent ? "" : "opacity-60"} style={accent ? { color: "var(--mod-accent)" } : null}>{icon}</span>
                <span>{n}</span>
            </div>
            <div className="text-white text-[14px] mb-1.5" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                {title}
            </div>
            <div className="text-white/55 text-[12.5px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>
                {body}
            </div>
        </div>
    );
}

// ===== FEATURES =====
const FEATURES = [
    { icon: Target, title: "Brand DNA strutturato", body: "Ogni cliente ha logo, palette, tono, target, hashtag, esempi, regole. L'AI conosce ogni brand come ci lavorasse da anni.", tag: "core" },
    { icon: Brain, title: "AI Copy multi-social", body: "1 brief → 6 post adattati per IG, FB, LinkedIn, X, TikTok, Threads. Ogni piattaforma rispetta hook, lunghezza, hashtag.", tag: "live" },
    { icon: ImageIcon, title: "AI Image brand-coded", body: "Grafiche social in 4 formati (square, portrait, story, landscape). Palette e tono coerenti col tuo brand.", tag: "live" },
    { icon: Calendar, title: "Calendario editoriale", body: "Drag&drop su mese/settimana. Eventi italiani precaricati. L'AI propone post per i giorni vuoti.", tag: "q1 2026" },
    { icon: MessageSquare, title: "Auto-publish multi-canale", body: "Pubblicazione programmata su IG, FB, LinkedIn, X, TikTok. Re-try automatico, notifica errori.", tag: "q2 2026" },
    { icon: BarChart3, title: "Analytics cross-social", body: "Impressions, engagement, click, conversioni. Dashboard agency-friendly + insight AI su cosa rifare.", tag: "q3 2026" },
];

function FeaturesSection() {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-100px" });
    return (
        <section id="features" ref={ref} className="relative px-5 md:px-12 py-24 md:py-32" data-testid="ads-features">
            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-14">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8, ease: EASE }}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5"
                        style={{ background: "var(--mod-accent-soft)", border: "1px solid var(--mod-accent)" }}
                    >
                        <span className="text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--mod-accent)", fontFamily: "JetBrains Mono, monospace" }}>
                            6 capacità · 1 cockpit
                        </span>
                    </motion.div>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.1, duration: 1, ease: EASE }}
                        className="text-white tracking-tight"
                        style={{ fontFamily: "Geist, Outfit, sans-serif", fontWeight: 200, fontSize: "clamp(2rem, 4.6vw, 4rem)", lineHeight: 1.05, letterSpacing: "-0.03em" }}
                    >
                        Tutto quello che un&apos;agenzia <span style={{ fontWeight: 600 }}>fa in una settimana</span>.<br />
                        In un&apos;ora.
                    </motion.h2>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                    {FEATURES.map((f, i) => (
                        <motion.div
                            key={f.title}
                            initial={{ opacity: 0, y: 30 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: 0.1 + i * 0.07, duration: 0.85, ease: EASE }}
                            whileHover={{ y: -4 }}
                            className="group relative rounded-2xl overflow-hidden"
                            style={{
                                background: "rgba(15,18,36,0.55)",
                                border: "1px solid rgba(255,255,255,0.07)",
                                backdropFilter: "blur(16px)",
                                WebkitBackdropFilter: "blur(16px)",
                                transition: "all 400ms cubic-bezier(0.16,1,0.3,1)",
                            }}
                        >
                            <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5">
                                <div className="flex gap-1.5">
                                    <span className="w-2 h-2 rounded-full" style={{ background: "rgba(255,95,87,0.55)" }} />
                                    <span className="w-2 h-2 rounded-full" style={{ background: "rgba(255,189,46,0.55)" }} />
                                    <span className="w-2 h-2 rounded-full" style={{ background: "rgba(39,201,63,0.55)" }} />
                                </div>
                                <span className="text-[9.5px] uppercase tracking-[0.18em] text-white/30" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                    {f.tag}
                                </span>
                            </div>
                            <div className="p-6">
                                <f.icon size={22} className="mb-4" style={{ color: "var(--mod-accent)" }} />
                                <div className="text-white text-[16px] mb-2" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                                    {f.title}
                                </div>
                                <p className="text-white/55 text-[13px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>
                                    {f.body}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ===== PRICING =====
const PLANS = [
    { name: "Trial", price: "0", brief: "Prova senza carta", features: ["1 brand", "20 AI copy/mese", "5 AI image/mese", "1 user"] },
    { name: "Solo", price: "29", brief: "Freelance / SMM singoli", features: ["3 brand", "200 AI copy/mese", "50 AI image/mese", "Auto-publish", "1 user"] },
    { name: "Studio", price: "79", brief: "Micro agenzie", popular: true, features: ["10 brand", "1.000 AI copy/mese", "250 AI image/mese", "Auto-publish", "3 user", "Approval workflow"] },
    { name: "Agency", price: "199", brief: "Agenzie 5+ persone", features: ["Brand illimitati", "5.000 AI copy/mese", "1.500 AI image/mese", "Auto-publish", "User illimitati", "Approval + audit log"] },
];

function PricingSection() {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-80px" });
    return (
        <section ref={ref} className="relative px-5 md:px-12 py-24 md:py-32" data-testid="ads-pricing">
            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-14">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8, ease: EASE }}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                    >
                        <span className="text-[10px] uppercase tracking-[0.22em] text-white/55" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                            piani · waitlist 50% sconto a vita
                        </span>
                    </motion.div>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.1, duration: 1, ease: EASE }}
                        className="text-white tracking-tight"
                        style={{ fontFamily: "Geist, Outfit, sans-serif", fontWeight: 200, fontSize: "clamp(2rem, 4.6vw, 4rem)", lineHeight: 1.05, letterSpacing: "-0.03em" }}
                    >
                        Paghi un <span style={{ fontWeight: 600 }}>aperitivo</span>.<br />
                        Risparmi un dipendente.
                    </motion.h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                    {PLANS.map((p, i) => (
                        <motion.div
                            key={p.name}
                            initial={{ opacity: 0, y: 30 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: 0.1 + i * 0.08, duration: 0.9, ease: EASE }}
                            className="relative p-6 rounded-2xl flex flex-col"
                            style={{
                                background: p.popular ? "linear-gradient(180deg, var(--mod-accent-soft) 0%, rgba(15,18,36,0.55) 100%)" : "rgba(15,18,36,0.55)",
                                border: p.popular ? "1px solid var(--mod-accent)" : "1px solid rgba(255,255,255,0.07)",
                                backdropFilter: "blur(16px)",
                                WebkitBackdropFilter: "blur(16px)",
                                boxShadow: p.popular ? "0 24px 60px -20px var(--mod-accent-glow)" : "none",
                            }}
                        >
                            {p.popular && (
                                <div
                                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] text-white"
                                    style={{ background: "linear-gradient(135deg, var(--mod-accent), var(--mod-accent-deep))", fontFamily: "JetBrains Mono, monospace" }}
                                >
                                    consigliato
                                </div>
                            )}
                            <div className="text-white text-[18px]" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                                {p.name}
                            </div>
                            <div className="text-white/45 text-[12.5px] mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                                {p.brief}
                            </div>
                            <div className="flex items-baseline gap-1 my-5">
                                <span className="text-white" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "44px", letterSpacing: "-0.03em", lineHeight: 1 }}>
                                    €{p.price}
                                </span>
                                <span className="text-white/45 text-[13px]">/mese</span>
                            </div>
                            <ul className="space-y-2 text-[12.5px] text-white/70 mb-6 flex-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                                {p.features.map((f) => (
                                    <li key={f} className="flex items-start gap-2">
                                        <Check size={13} className="mt-0.5 flex-none" style={{ color: "var(--mod-accent)" }} />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <a
                                href="#waitlist"
                                data-testid={`ads-plan-cta-${p.name.toLowerCase()}`}
                                className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-[13px] transition"
                                style={p.popular ? {
                                    background: "linear-gradient(135deg, var(--mod-accent), var(--mod-accent-deep))",
                                    color: "#fff",
                                    fontFamily: "Geist, sans-serif",
                                    letterSpacing: "0.02em",
                                    boxShadow: "0 10px 30px -8px var(--mod-accent-glow)",
                                } : {
                                    border: "1px solid rgba(255,255,255,0.15)",
                                    color: "#fff",
                                    fontFamily: "Geist, sans-serif",
                                    letterSpacing: "0.02em",
                                }}
                            >
                                Entra in waitlist <ArrowRight size={13} />
                            </a>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ===== WAITLIST FORM =====
function WaitlistSection() {
    const [form, setForm] = useState({ name: "", email: "", company: "", role: "agency", message: "" });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true); setError("");
        try {
            await api.post("/public/waitlist", { ...form, product: "totalklickads" });
            setDone(true);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="waitlist" className="relative px-5 md:px-12 py-24 md:py-32" data-testid="ads-waitlist">
            <div className="max-w-2xl mx-auto">
                <div className="text-center mb-10">
                    <Sparkles size={28} className="mx-auto mb-5" style={{ color: "var(--mod-accent)" }} />
                    <h2
                        className="text-white tracking-tight mb-4"
                        style={{ fontFamily: "Geist, Outfit, sans-serif", fontWeight: 200, fontSize: "clamp(2rem, 4.6vw, 3.6rem)", lineHeight: 1.05, letterSpacing: "-0.03em" }}
                    >
                        Resta tra i <span style={{ fontWeight: 600 }}>primi 100</span>.
                    </h2>
                    <p className="text-white/65 text-[14px] max-w-xl mx-auto" style={{ fontFamily: "Outfit, sans-serif" }}>
                        50% di sconto a vita. Onboarding 1-to-1 con noi. Accesso anticipato a calendario e auto-publish.
                    </p>
                </div>
                {done ? (
                    <div
                        className="text-center py-12 rounded-2xl"
                        style={{
                            background: "var(--mod-accent-soft)",
                            border: "1px solid var(--mod-accent)",
                        }}
                        data-testid="ads-waitlist-done"
                    >
                        <Check size={36} className="mx-auto mb-4" style={{ color: "var(--mod-accent)" }} />
                        <div className="text-white text-[18px] mb-2" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                            Sei in lista!
                        </div>
                        <p className="text-white/60 text-[13px]" style={{ fontFamily: "Outfit, sans-serif" }}>
                            Ti scriviamo non appena apriamo gli accessi. Massimo qualche settimana.
                        </p>
                    </div>
                ) : (
                    <form
                        onSubmit={submit}
                        className="rounded-2xl p-6 md:p-8 space-y-3"
                        style={{
                            background: "rgba(15,18,36,0.6)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            backdropFilter: "blur(20px)",
                            WebkitBackdropFilter: "blur(20px)",
                        }}
                        data-testid="ads-waitlist-form"
                    >
                        <div className="grid sm:grid-cols-2 gap-3">
                            <input
                                required
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Nome e cognome *"
                                className="ads-input"
                                data-testid="ads-form-name"
                            />
                            <input
                                required
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                placeholder="Email *"
                                className="ads-input"
                                data-testid="ads-form-email"
                            />
                        </div>
                        <input
                            value={form.company}
                            onChange={(e) => setForm({ ...form, company: e.target.value })}
                            placeholder="Azienda / agenzia"
                            className="ads-input"
                            data-testid="ads-form-company"
                        />
                        <select
                            value={form.role}
                            onChange={(e) => setForm({ ...form, role: e.target.value })}
                            className="ads-input"
                            data-testid="ads-form-role"
                        >
                            <option value="agency">Agenzia social</option>
                            <option value="freelance">Freelance / SMM</option>
                            <option value="brand">Brand interno</option>
                            <option value="other">Altro</option>
                        </select>
                        <textarea
                            rows={3}
                            value={form.message}
                            onChange={(e) => setForm({ ...form, message: e.target.value })}
                            placeholder="Quanti brand gestisci? Quale piattaforma usi oggi?"
                            className="ads-input"
                            data-testid="ads-form-message"
                        />
                        {error && <div className="text-rose-400 text-[12px]">{error}</div>}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full text-white text-[14px] transition disabled:opacity-50"
                            style={{
                                background: "linear-gradient(135deg, var(--mod-accent), var(--mod-accent-deep))",
                                fontFamily: "Geist, sans-serif",
                                letterSpacing: "0.04em",
                                boxShadow: "0 18px 50px -10px var(--mod-accent-glow)",
                            }}
                            data-testid="ads-waitlist-submit"
                        >
                            {loading ? "Invio…" : <>Entra in waitlist <ArrowRight size={14} /></>}
                        </button>
                    </form>
                )}
            </div>
            <style>{`
                .ads-input { width:100%; padding:12px 14px; border-radius:12px; border:1px solid rgba(255,255,255,0.1); background:rgba(255,255,255,0.03); color:#fff; font-family:'Geist','Outfit',sans-serif; font-size:13.5px; transition:all .2s; }
                .ads-input:focus { outline:none; border-color:var(--mod-accent); background:rgba(255,255,255,0.05); box-shadow:0 0 0 3px var(--mod-accent-soft); }
                .ads-input::placeholder { color:rgba(255,255,255,0.3); }
                .ads-input option { background:#0F1224; color:#fff; }
            `}</style>
        </section>
    );
}

// ===== FAQ =====
const FAQ = [
    { q: "Devo creare il Brand DNA per ogni cliente?", a: "Sì, ma è un investimento una tantum di 10 minuti. Da quel momento l'AI conoscerà tono, palette, target, regole. Niente più 'spiegami il cliente' a ogni post." },
    { q: "Funziona davvero in italiano?", a: 'Sì. AI Copy è ottimizzato per il mercato italiano (lingua, riferimenti culturali, eventi, festività). Per testarlo: chiedi un post "tono Bar di Napoli". Vedrai.' },
    { q: "L'auto-publish quando arriva?", a: "Q2 2026. Stiamo completando Meta App Review (IG/FB). LinkedIn, X, TikTok seguono. Nel frattempo: scarichi/copi e pubblichi a mano in 2 minuti." },
    { q: "Posso provare prima di pagare?", a: "Sì, il piano Trial è gratis senza carta: 1 brand, 20 copy, 5 image al mese. Se sei in waitlist hai il 50% di sconto a vita su tutti i piani pagati." },
    { q: "Le immagini sono utilizzabili commercialmente?", a: "Sì. Sono generate via Gemini Nano Banana e i diritti sono tuoi. Nessun watermark, no royalty." },
];

function FAQSection() {
    const [open, setOpen] = useState(0);
    return (
        <section className="relative px-5 md:px-12 py-24 md:py-32" data-testid="ads-faq">
            <div className="max-w-3xl mx-auto">
                <div className="text-center mb-12">
                    <h2
                        className="text-white tracking-tight"
                        style={{ fontFamily: "Geist, Outfit, sans-serif", fontWeight: 200, fontSize: "clamp(2rem, 4.4vw, 3.6rem)", lineHeight: 1.05, letterSpacing: "-0.03em" }}
                    >
                        Domande <span style={{ fontWeight: 600 }}>frequenti</span>.
                    </h2>
                </div>
                <div className="space-y-2">
                    {FAQ.map((f, i) => {
                        const isOpen = open === i;
                        return (
                            <button
                                key={i}
                                type="button"
                                onClick={() => setOpen(isOpen ? -1 : i)}
                                className="w-full text-left rounded-xl px-5 py-4 transition"
                                style={{
                                    background: isOpen ? "var(--mod-accent-soft)" : "rgba(15,18,36,0.5)",
                                    border: `1px solid ${isOpen ? "var(--mod-accent)" : "rgba(255,255,255,0.07)"}`,
                                }}
                                data-testid={`ads-faq-${i}`}
                            >
                                <div className="flex items-center justify-between gap-4">
                                    <span className="text-white text-[14.5px]" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                                        {f.q}
                                    </span>
                                    <ChevronDown size={16} className="text-white/45 flex-none" style={{ transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
                                </div>
                                {isOpen && (
                                    <p className="mt-3 text-white/65 text-[13.5px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>
                                        {f.a}
                                    </p>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

// ===== MAIN =====
export default function TotalKlickADS() {
    useEffect(() => {
        document.title = "TotalKlickADS — La tua agenzia social AI sempre attiva";
    }, []);

    return (
        <ModuleAccent
            module="ads"
            as="div"
            className="min-h-screen w-full overflow-x-hidden"
            style={{
                background: "#05050F",
                color: "#fff",
                fontFamily: "Geist, Outfit, sans-serif",
            }}
            data-testid="ads-landing"
        >
            <CinematicNav />
            <Hero />
            <ADSWorkflowScene />
            <FeaturesSection />
            <PricingSection />
            <WaitlistSection />
            <FAQSection />
            <Footer variant="hub" />
        </ModuleAccent>
    );
}
